import { Builder } from '@builder.io/react'
import React from 'react'

import { HeadingClass, HeadingType } from 'enums'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

interface Specification {
  name: string
  description: string
  backgroundColor: string
}

interface ImageItem {
  imgSrc: any
  alt: string
}

interface SpecificationSectionProp {
  backgroundColor: string
  headingText: string
  headingTextColor: string
  headingSubText: string
  headingSubTextColor: string
  headingSubTextEnabled: boolean
  specifications: Specification[]
  specificationDescriptionEnabled: boolean
  specificationTextColor: string
  specificationDescriptionColor: string
  images: ImageItem[]
  headingType: HeadingType
  headingClass: HeadingClass
}

// https://tailwindui.com/components/ecommerce/components/product-features#component-6bf262ecea63105e5f1fc57ec12057f9
const SpecificationSection = ({
  backgroundColor,
  headingText,
  headingSubText,
  headingSubTextColor,
  headingSubTextEnabled,
  headingTextColor,
  specificationDescriptionColor,
  specificationDescriptionEnabled,
  specificationTextColor,
  specifications,
  images,
  headingType,
  headingClass
}: SpecificationSectionProp) => {
  const renderSubHeading = () => {
    if (!headingSubTextEnabled) return null

    return (
      <p className="mt-4 text-gray-500" style={{ color: headingSubTextColor }}>
        {headingSubText}
      </p>
    )
  }

  const renderSpecificationDescription = (spec: string) => {
    if (!specificationDescriptionEnabled) return null

    return (
      <dd className="mt-2 text-sm text-gray-500" style={{ color: specificationDescriptionColor }}>
        {spec}
      </dd>
    )
  }

  return (
    <div className="bg-white" style={{ backgroundColor }}>
      <div className="mx-auto grid max-w-2xl grid-cols-1 items-center gap-y-16 gap-x-8 py-5 px-4 sm:px-6 sm:py-10 lg:max-w-7xl lg:grid-cols-2 lg:px-8">
        <div>
          <CustomHeading
            classList="font-bold tracking-tight text-gray-900"
            headingClass={headingClass}
            headingType={headingType}
            color={headingTextColor}
          >
            {headingText}
          </CustomHeading>
          {renderSubHeading()}
          <dl className="mt-16 grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 sm:gap-y-16 lg:gap-x-8">
            {specifications?.map((spec, s) => (
              // <div key={spec.name} className="border-t border-gray-200 p-4" style={{ backgroundColor: spec.backgroundColor }}>
              <div key={s} className="rounded-lg p-4" style={{ backgroundColor: spec.backgroundColor }}>
                <dt className="font-medium text-gray-900" style={{ color: specificationTextColor }}>
                  {spec.name}
                </dt>
                {renderSpecificationDescription(spec.description)}
              </div>
            ))}
          </dl>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {images?.map((img, i) => (
            <img
              key={i}
              src={img.imgSrc || 'https://tailwindui.com/img/ecommerce-images/product-feature-03-detail-01.jpg'}
              alt={img.alt}
              className="rounded-lg bg-gray-100"
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default SpecificationSection

Builder.registerComponent(SpecificationSection, {
  name: 'SpecificationSection',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'green'
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Simple Item'
    },
    {
      name: 'headingSubText',
      type: 'text',
      defaultValue: 'Your Dog is everything. Take care of your Dog.'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'headingSubTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'headingSubTextEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'specifications',
      type: 'list',
      defaultValue: [{ name: 'Good Food', description: 'Made from scratch', imageSrc: null, imageAlt: 'Our food' }],
      subFields: [
        { name: 'name', type: 'string', defaultValue: 'Healthy skin' },
        { name: 'description', type: 'string', defaultValue: 'Comes with good food' },
        { name: 'backgroundColor', type: 'color', defaultValue: 'white' }
      ]
    },
    {
      name: 'specificationTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'specificationDescriptionEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'specificationDescriptionColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'images',
      type: 'list',
      defaultValue: [{ imgSrc: null, defaultValue: 'img alt' }],
      subFields: [
        { name: 'imgSrc', type: 'file', defaultValue: null, allowedFileTypes: ['jpeg', 'png', 'svg'] },
        { name: 'alt', type: 'string', defaultValue: 'img alt' }
      ]
    }
  ]
})
